import React, { useState, useEffect } from 'react'
import { Box, Typography, Modal, CircularProgress, Button, IconButton, Stack } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import { useNavigate } from 'react-router-dom'
import GradientCircularProgressIndicator from '../../../../components/GradientCircularProgressIndicator'
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { ClockPicker } from '@mui/lab'

const GeneratingTemplateModal = ({ 
    open, 
    onClose, 
    loading,
    success,
    error,
    warning,
    errorMessage: propErrorMessage,
    successMessage: propSuccessMessage,
    warningMessage: propWarningMessage,
    warningSubMessage: propWarningSubMessage,
    onSuccess,
    onError,
    onWarning,
    onCancel,
    buttonText,
    showConfirmation = false,
    onConfirm,
    confirmButtonText = 'Continue',
    cancelButtonText = 'Cancel',
    loadingMessage: propLoadingMessage,
    loadingSubMessage: propLoadingSubMessage
}) => {
    const theme = useTheme()
    const navigate = useNavigate()
    
    const [generationState, setGenerationState] = useState('')
    const [generationMessage, setGenerationMessage] = useState('')

    // Use prop messages if provided, otherwise use internal state
    const errorMessage = propErrorMessage || generationMessage
    const successMessage = propSuccessMessage || generationMessage
    const warningMessage = propWarningMessage || generationMessage

    useEffect(() => {
        if (success) {
            setGenerationState('success')
            setGenerationMessage('Your template is currently being created')
            
            // Auto-close after 3 seconds on success
            const autoCloseTimeout = setTimeout(() => {
                if (onClose) {
                    onClose()
                }
            }, 3000)
            
            if (onSuccess) {
                onSuccess()
            }
            
            // Cleanup timeout on unmount
            return () => clearTimeout(autoCloseTimeout)
        }
    }, [success, onSuccess, onClose])

    useEffect(() => {
        if (error) {
            setGenerationState('error')
            setGenerationMessage('Template creation failed. Please try again.')
            if (onError) {
                onError()
            }
        }
    }, [error, onError])

    useEffect(() => {
        if (warning) {
            setGenerationState('warning')
            setGenerationMessage('Your template is still processing')
            if (onWarning) {
                onWarning()
            }
        }
    }, [warning])

    const handleCloseModal = () => {
        onClose()
    }

    const handleRetry = () => {
        // Reset error state and close modal to allow retry
        setGenerationMessage('')
        onClose()
    }

    const renderContent = () => {
        if (success) {
            return (
                <Stack sx={{ display: 'flex', alignItems: 'center', gap: '16px', flexGrow: 1, width: '100%'}}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <CheckCircleIcon sx={{ fontSize: '48px', color: '#66bb6a' }} />
                            <Typography variant="h6" sx={{ textAlign: 'center' }}>
                                {successMessage}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                                This can take up to 2 hours.
                            </Typography>
                            <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                                You will get an email when it is ready.
                            </Typography>
                        </Box>
                    </Box>
                    <Button
                        onClick={onClose}
                        variant="contained"
                        sx={{
                            backgroundColor:'#1F477D',
                            color: '#FFFFFF',
                            padding: '13px 28px',
                            borderRadius: '8px',
                            fontSize: '16px',
                            fontWeight: 500,
                            lineHeight: '24px',
                            textTransform: 'none',
                            minWidth: '300px',
                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                            '&:hover': {
                                backgroundColor: '#1a3a66',
                            },
                            '&:disabled': {
                                backgroundColor: '#E5E7EB',
                                color: '#6B7280',
                            },
                        }}
                    >
                        To Templates Page
                    </Button>
                </Stack>
            )
        }
        else if (error) {
            return (
                <Stack sx={{ display: 'flex', alignItems: 'center', gap: '16px', flexGrow: 1, width: '100%'}}>
                    <ErrorIcon sx={{ fontSize: '48px', color: '#b00020' }} />
                    <Typography variant="h6" sx={{ textAlign: 'center' }}>
                        Error
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                        {errorMessage}
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={handleRetry}
                        sx={{ mt: 2 }}
                    >
                        {buttonText || 'Try Again'}
                    </Button>
                </Stack>
            )
        }
        else if (loading) {
            // Show confirmation if this is the initial warning state
            if (showConfirmation && onConfirm) {
                return (
                    <Stack sx={{ display: 'flex', alignItems: 'center', gap: '16px', flexGrow: 1, width: '100%'}}>
                        <Typography variant="h6" sx={{ textAlign: 'center' }}>
                            {propLoadingMessage || 'Beginning template creation...'}
                        </Typography>
                        <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary', px: 2 }}>
                            {propLoadingSubMessage || 'This may take a moment.'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={onClose}
                                sx={{
                                    borderRadius: 1.5,
                                    borderColor: '#E5E7EB',
                                    color: '#475569',
                                    fontWeight: 500,
                                    fontSize: 14,
                                    px: 3,
                                    py: 1,
                                    textTransform: 'none',
                                    '&:hover': {
                                        borderColor: '#CBD5E1',
                                        backgroundColor: '#F8FAFC'
                                    }
                                }}
                            >
                                {cancelButtonText}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={onConfirm}
                                sx={{
                                    borderRadius: 1.5,
                                    backgroundColor: '#3B82F6',
                                    fontWeight: 500,
                                    fontSize: 14,
                                    px: 3,
                                    py: 1,
                                    textTransform: 'none',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: '#2563EB',
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                {confirmButtonText}
                            </Button>
                        </Box>
                    </Stack>
                )
            } else {
                // Show normal loading state
                return (
                    <Stack sx={{ display: 'flex', alignItems: 'center', gap: '16px', flexGrow: 1, width: '100%'}}>
                        <GradientCircularProgressIndicator size={48} />
                        <Typography variant="h6" sx={{ textAlign: 'center' }}>
                            {propLoadingMessage || 'Beginning template creation...'}
                        </Typography>
                        <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                            {propLoadingSubMessage || 'This may take a moment.'}
                        </Typography>
                    </Stack>
                )
            }
        }
        else if (warning) {
            // If onCancel is provided, show both buttons; otherwise show single button (legacy behavior)
            if (onCancel) {
                return (
                    <Stack sx={{ display: 'flex', alignItems: 'center', gap: '16px', flexGrow: 1, width: '100%'}}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                <AccessTimeIcon sx={{ width: '48px', height: '48px', color: '#D97706', backgroundColor: '#FEF3C7', borderRadius: '50%', padding: '12px' }} />
                            </Box>
                            <Box>
                                <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 600, fontSize: '18px', lineHeight: '24px' }}>
                                    {warningMessage}
                                </Typography>
                                <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary', px: 2 }}>
                                    {propWarningSubMessage || "We'll send you an email once it's ready."}
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                            <Button
                                onClick={onCancel}
                                variant="outlined"
                                sx={{
                                    borderRadius: 1.5,
                                    borderColor: '#E5E7EB',
                                    color: '#475569',
                                    fontWeight: 500,
                                    fontSize: 14,
                                    px: 3,
                                    py: 1,
                                    textTransform: 'none',
                                    minWidth: '100px',
                                    '&:hover': {
                                        borderColor: '#CBD5E1',
                                        backgroundColor: '#F8FAFC'
                                    }
                                }}
                            >
                                {cancelButtonText || 'Cancel'}
                            </Button>
                            <Button
                                onClick={onWarning || onClose}
                                variant="contained"
                                sx={{
                                    backgroundColor:'#1F477D',
                                    color: '#FFFFFF',
                                    padding: '13px 28px',
                                    borderRadius: '8px',
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    lineHeight: '24px',
                                    textTransform: 'none',
                                    minWidth: '200px',
                                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                                    '&:hover': {
                                        backgroundColor: '#1a3a66',
                                    },
                                    '&:disabled': {
                                        backgroundColor: '#E5E7EB',
                                        color: '#6B7280',
                                    },
                                }}
                            >
                                {buttonText || 'Got it!'}
                            </Button>
                        </Box>
                    </Stack>
                )
            } else {
                // Legacy behavior - single button centered
                return (
                    <Stack sx={{ display: 'flex', alignItems: 'center', gap: '16px', flexGrow: 1, width: '100%'}}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                <AccessTimeIcon sx={{ width: '48px', height: '48px', color: '#D97706', backgroundColor: '#FEF3C7', borderRadius: '50%', padding: '12px' }} />
                            </Box>
                            <Box>
                                <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 600, fontSize: '18px', lineHeight: '24px' }}>
                                    {warningMessage}
                                </Typography>
                                <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary', px: 2 }}>
                                    {propWarningSubMessage || "We'll send you an email once it's ready."}
                                </Typography>
                            </Box>
                        </Box>
                        <Button
                            onClick={onWarning || onClose}
                            variant="contained"
                            sx={{
                                backgroundColor:'#1F477D',
                                color: '#FFFFFF',
                                padding: '13px 28px',
                                borderRadius: '8px',
                                fontSize: '16px',
                                fontWeight: 500,
                                lineHeight: '24px',
                                textTransform: 'none',
                                minWidth: '300px',
                                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                                '&:hover': {
                                    backgroundColor: '#1a3a66',
                                },
                                '&:disabled': {
                                    backgroundColor: '#E5E7EB',
                                    color: '#6B7280',
                                },
                            }}
                        >
                            {buttonText || 'Got it!'}
                        </Button>
                    </Stack>
                )
            }
        }   
        else {
            return null
        }
    }

    return (
        <Modal
            open={open}
            onClose={handleCloseModal} // Only allow closing on error
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Box
                sx={{
                    width: '384px',
                    minHeight: '240px',
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    padding: '32px',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '24px'
                }}
            >
                {/* Close button only shown on error */}
                {generationState === 'error' && (
                    <IconButton
                        onClick={handleCloseModal}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                )}
                {(generationState === 'success' || generationState === 'warning') && (
                    <IconButton
                        onClick={onClose}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                )}
                {/* Main Content Container */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '160px' }}>
                    {renderContent()}
                </Box>
            </Box>
        </Modal>
    )
}

export default GeneratingTemplateModal 